import React, { useCallback, useMemo, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faShoppingBag, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import Select from "react-select";
import { ValidationMode } from "../types/validation-mode-type.ts";
import ValidationPack from "@/pages/takeoff/layouts/validation/models/validation-pack.ts";
import useViewerItemsSelectionContext from "@/services/context/viewer/items-selection/use-viewer-items-selection-context.ts";
import ProjectLockStatus from "@/models/enums/project-lock-status.ts";
import Button from "@/components/buttons/button";
import { Case, Switch } from "@/components/switch";
import thermalsType from "@/pages/takeoff/layouts/validation/layouts/details/models/thermals-type.ts";
import validationsTypes from "@/pages/takeoff/layouts/validation/layouts/details/models/validations-type.ts";
import { ShellThermalListboxItem } from "@/pages/takeoff/layouts/validation/layouts/details/models/shell-thermal-item.ts";
import { ShellValidationListboxItem } from "@/pages/takeoff/layouts/validation/layouts/details/models/shell-validation-item.ts";
import useProjectContext from "@/services/context/project/use-project-context.ts";
import useValidationContext from "@/pages/takeoff/layouts/validation/context/validation/use-validation-context.ts";
import usePackValidationContext from "../context/use-pack-validation-context.ts";
import useViewerItemsColorContext from "@/services/context/viewer/items-color/use-viewer-items-color-context.ts";
import FormattedObject from "@/models/interfaces/viewer/formatted-object.ts";
import Card from "@/components/card";

interface PackSelectionProps {
    pack: ValidationPack;
    mode: ValidationMode;
}

const PackSelection: React.FC<PackSelectionProps> = ({ pack, mode }) => {
    const { projectLockStatus } = useProjectContext();
    const { selectedItems, clearSelection, subscribeSelectionChanged } = useViewerItemsSelectionContext();
    const { packsData } = useValidationContext();
    const { upsertShellModification } = usePackValidationContext();
    const { changeColor } = useViewerItemsColorContext();

    const [selectedValidationShell, setSelectedValidationShell] = useState<ShellValidationListboxItem | null>(null);
    const [selectedThermalShell, setSelectedThermalShell] = useState<ShellThermalListboxItem | null>(null);

    const packObjectTypos = useMemo(() => packsData.get(pack.id)?.objectTypos ?? [], [packsData, pack.id]);

    const updateSelectedShells = useCallback((items: FormattedObject[]) => {
        if (items.length === 0) {
            setSelectedThermalShell(null);
            setSelectedValidationShell(null);
            return;
        }

        const lastItem = items[items.length - 1];
        const match = packObjectTypos.find(x => x.object.workspaceUid === lastItem.workspaceUid && x.object.objectUid === lastItem.objectUid)
            ?? [...packsData.values()].flatMap(p => p.objectTypos).find(x => x.object.workspaceUid === lastItem.workspaceUid && x.object.objectUid === lastItem.objectUid);

        if (match) {
            if (mode === "thermal") {
                setSelectedThermalShell(thermalsType.find(thermal => thermal.keyName === match.thermalTypo) ?? null);
            } else {
                setSelectedValidationShell(validationsTypes.find(validation => validation.keyName === match.shellTypo) ?? null);
            }
        }
    }, [mode, packObjectTypos, packsData]);

    useEffect(() => 
        subscribeSelectionChanged((event) => {
            if (event.action === "clear") {
                setSelectedThermalShell(null);
                setSelectedValidationShell(null);
            } else if (event.action === "add" && event.item) {
                const {item} = event;
                const match = packObjectTypos.find(x => x.object.workspaceUid === item.workspaceUid && x.object.objectUid === item.objectUid)
                    ?? [...packsData.values()].flatMap(p => p.objectTypos).find(x => x.object.workspaceUid === item.workspaceUid && x.object.objectUid === item.objectUid);

                if (match) {
                    if (mode === "thermal") {
                        setSelectedThermalShell(thermalsType.find(thermal => thermal.keyName === match.thermalTypo) ?? null);
                    } else {
                        setSelectedValidationShell(validationsTypes.find(validation => validation.keyName === match.shellTypo) ?? null);
                    }
                }
            }
        }), [subscribeSelectionChanged, packObjectTypos, packsData, mode, selectedItems.length]);

    useEffect(() => {
        updateSelectedShells(selectedItems);
    }, [mode, selectedItems, updateSelectedShells]);

    const updateThermalType = useCallback((selected: ShellThermalListboxItem): void => {
        if (!selected || !selectedItems.length)
            return;

        selectedItems.forEach(item => {
            const match = packObjectTypos.find(x => x.object.workspaceUid === item.workspaceUid && x.object.objectUid === item.objectUid)
                ?? [...packsData.values()].flatMap(p => p.objectTypos).find(x => x.object.workspaceUid === item.workspaceUid && x.object.objectUid === item.objectUid);
            if (match) {
                upsertShellModification(match.shellId, undefined, selected.keyName);
            }
        });

        changeColor(selectedItems, selected.color, undefined, selected.keyName);
        clearSelection();
    }, [selectedItems, packObjectTypos, packsData, upsertShellModification, changeColor, clearSelection]);

    const updateValidationType = useCallback((selected: ShellValidationListboxItem): void => {
        if (!selected || !selectedItems.length)
            return;

        selectedItems.forEach(item => {
            const match = packObjectTypos.find(x => x.object.workspaceUid === item.workspaceUid && x.object.objectUid === item.objectUid)
                ?? [...packsData.values()].flatMap(p => p.objectTypos).find(x => x.object.workspaceUid === item.workspaceUid && x.object.objectUid === item.objectUid);
            if (match) {
                upsertShellModification(match.shellId, selected.keyName, undefined);
            }
        });

        changeColor(selectedItems, selected.color, selected.keyName, undefined);
        clearSelection();
    }, [selectedItems, packObjectTypos, packsData, upsertShellModification, changeColor, clearSelection]);
    
    if (projectLockStatus !== ProjectLockStatus.LOCK_OWNED)
        return <></>;

    return (
        <Card transparent={true} additionalContentClasses='flex flex-row items-center gap-x-6' additionalClasses='px-4 pt-3 pb-4 min-w-2xl'>
            <div className="flex flex-col gap-y-2 flex-1">
                <span className="text-base font-semibold leading-6 text-gray-900 whitespace-nowrap">
                    Sélectionner les coques
                </span>

                <div className="flex flex-row items-center gap-x-2">
                    <div
                        className='flex flex-row gap-x-2 items-center text-white text-sm font-semibold bg-green-600 rounded-md px-4 py-1.25'>
                        <FontAwesomeIcon icon={faShoppingBag}/>
                        <span>{selectedItems.length}</span>
                    </div>

                    <Button variant='danger' size='md'
                            title="Effacer les éléments sélectionnés"
                            onClick={clearSelection}>
                        <FontAwesomeIcon icon={faTrashCan}/>
                    </Button>

                    {mode === "thermal" ? (
                        <>
                            <span>{selectedThermalShell?.label}</span>

                            {selectedThermalShell?.description && (
                                <>
                                    <FontAwesomeIcon icon={faCircleInfo}
                                                     className='text-indigo-600 text-xl'/>
                                    <span className="text-gray-500">{selectedThermalShell.description}</span>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <span>{selectedValidationShell?.label}</span>

                            {selectedValidationShell?.description && (
                                <>
                                    <FontAwesomeIcon icon={faCircleInfo}
                                                     className='text-indigo-600 text-xl'/>
                                    <span className="text-gray-500 text-sm">{selectedValidationShell.description}</span>
                                </>
                            )}
                        </>
                    )}

                </div>
            </div>

            <div className="flex flex-col gap-y-2 min-w-50">
                <Switch value={mode}>
                    <Case value='thermal'>
                        <span className="text-base font-semibold leading-6 text-gray-900 text-right">
                            Modifier la caractéristique thermique
                        </span>
                        <Select options={thermalsType}
                                value={selectedThermalShell}
                                isSearchable={false}
                                onChange={(o) => {
                                    if (o) updateThermalType(o)
                                }}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => String(option.keyName)}
                                menuPlacement='top' menuPosition='fixed' menuPortalTarget={document.body}
                                placeholder={null}
                                isDisabled={selectedThermalShell === null}
                                classNames={{
                                    control: () => "!min-h-0 !rounded-md !border-gray-200",
                                    menu: () => "text-sm",
                                    menuList: () => "text-sm",
                                    singleValue: () => "text-sm",
                                    placeholder: () => "text-sm",
                                    indicatorsContainer: () => "-my-1",
                                }}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 50,
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 50,
                                    }),
                                }}
                                formatOptionLabel={(option) => (
                                    <div className="flex flex-row items-center gap-x-2">
                                        <span
                                            className="size-3 rounded-full"
                                            style={{backgroundColor: option.color}}/>
                                        <span>{option.label}</span>
                                    </div>
                                )}/>
                    </Case>
                    <Case value='functional'>
                        <span className="text-base font-semibold leading-6 text-gray-900 text-right">
                            Modifier le type de coque
                        </span>
                        <Select options={validationsTypes}
                                value={selectedValidationShell}
                                isSearchable={false}
                                onChange={(o) => {
                                    if (o) updateValidationType(o)
                                }}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => String(option.keyName)}
                                menuPlacement='top' menuPosition='fixed' menuPortalTarget={document.body}
                                placeholder={null}
                                isDisabled={selectedValidationShell === null}
                                classNames={{
                                    control: () => "!min-h-0 !rounded-md !border-gray-200",
                                    menu: () => "text-sm",
                                    menuList: () => "text-sm",
                                    singleValue: () => "text-sm",
                                    placeholder: () => "text-sm",
                                    indicatorsContainer: () => "-my-1",
                                }}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 50,
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 50,
                                    }),
                                }}

                                formatOptionLabel={(option) => (
                                    <div className="flex flex-row items-center gap-x-2">
                                        <span
                                            className="size-3 rounded-full"
                                            style={{backgroundColor: option.color}}/>
                                        <span>{option.label}</span>
                                    </div>
                                )}/>
                    </Case>
                </Switch>
            </div>
        </Card>
    );
};

export default PackSelection;
